import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Box,
  ModalFooter,
  Text,
  Flex,
  Spinner
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { GiIsland } from 'react-icons/gi'
import { IoIosAddCircle } from 'react-icons/io'

import useParametrizationConfigPurchase from '@hook/parametrization/use-config-purchase'
import useFilters from '@hook/shared/use-filters'

import Filter from '@component/Shared/Filter'

import { getCategoriasFornecedores } from '@service/ParametroCompra'

// Tipagem
interface IValue {
  label: string
  value: string | number
}

interface IFormData {
  compradorPrincipal: IValue | null
  compradorFerias: IValue | null
  fornecedor: IValue | null
  de: string
  ate: string
}

const ModalCreateFeiras: FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { data } = useFilters()
  const { createFerias, updateFeriasOb, selectedFerias, editDataFerias } =
    useParametrizationConfigPurchase()

  const initialState: IFormData = {
    compradorPrincipal: null,
    compradorFerias: null,
    fornecedor: null,
    de: '',
    ate: ''
  }

  const [formData, setFormData] = useState<IFormData>(initialState)
  const [fornecedoresOptions, setFornecedoresOptions] = useState<IValue[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const onUpdate = (field: keyof IFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const validate = () => {
    const newErrors: string[] = []
    if (!formData.compradorPrincipal) {
      newErrors.push('Selecione um Comprador Backup.')
    }
    if (!formData.compradorFerias) {
      newErrors.push('Selecione um Comprador de Férias.')
    }
    if (!formData.fornecedor) newErrors.push('Selecione um Fornecedor.')
    if (!formData.de) newErrors.push('Data inicial é obrigatória.')
    if (!formData.ate) newErrors.push('Data final é obrigatória.')
    if (formData.de && formData.ate && formData.de > formData.ate) {
      newErrors.push('Data inicial não pode ser maior que a final.')
    }
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const clearForm = () => {
    setFormData(initialState)
    setErrors([])
  }

  // Validar se todos os campos obrigatórios estão preenchidos
  const isFormValid = () => {
    return (
      formData.compradorPrincipal && // Comprador Backup selecionado
      formData.compradorFerias && // Comprador Férias selecionado
      formData.fornecedor && // Fornecedor selecionado
      formData.de && // Data inicial preenchida
      formData.ate && // Data final preenchida
      formData.de <= formData.ate // Data inicial menor ou igual à final
    )
  }

  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = async () => {
    if (isSaving) return
    if (validate()) {
      const payload = {
        id: selectedFerias,
        compradorPrincipalId: formData.compradorPrincipal?.value,
        compradorFeriasId: formData.compradorFerias?.value,
        fornecedorId: Number(formData.fornecedor?.value),
        dataInicio: formData.de,
        dataFim: formData.ate
      }
      setIsSaving(true)
      const success =
        selectedFerias !== 0
          ? await editDataFerias(payload)
          : await createFerias(payload)
      setIsSaving(false)
      // Fecha só no sucesso; no erro o toast explica e o formulário continua preenchido
      if (success) {
        onClose()
        clearForm()
      }
    }
  }

  useEffect(() => {
    if (!isOpen) return
    setIsLoading(true)

    const fetchFornecedores = async () => {
      try {
        const resp = await getCategoriasFornecedores()
        const fornecedores =
          resp.data?.data?.map((f: any) => ({
            label: `${f.supplierId} - ${f.supplierDescription}`,
            value: f.supplierId
          })) || []
        setFornecedoresOptions(fornecedores)

        if (updateFeriasOb) {
          const sameId = (a: any, b: any) =>
            a != null && String(a) === String(b)
          // Comprador fora da lista de usuários: monta a opção a partir do próprio
          // registro, para o campo não ficar vazio e o Salvar continuar habilitado
          const fromRecord = (
            id: any,
            email?: string,
            nome?: string
          ): IValue | null =>
            id != null
              ? { value: id, label: email || nome || String(id) }
              : null

          const compradorPrincipal =
            data.users.find(u =>
              sameId(u.value, updateFeriasOb.compradorPrincipalId)
            ) ||
            fromRecord(
              updateFeriasOb.compradorPrincipalId,
              updateFeriasOb.compradorPrincipalEmail,
              updateFeriasOb.compradorPrincipalNome
            )
          const compradorFerias =
            data.users.find(u =>
              sameId(u.value, updateFeriasOb.compradorFeriasId)
            ) ||
            fromRecord(
              updateFeriasOb.compradorFeriasId,
              updateFeriasOb.compradorFeriasEmail,
              updateFeriasOb.compradorFeriasNome
            )
          const fornecedor =
            fornecedores.find((f: { value: any }) =>
              sameId(f.value, updateFeriasOb.fornecedorId)
            ) ||
            (updateFeriasOb.fornecedorId != null
              ? {
                  value: updateFeriasOb.fornecedorId,
                  label: updateFeriasOb.fornecedorNome
                    ? `${updateFeriasOb.fornecedorId} - ${updateFeriasOb.fornecedorNome}`
                    : String(updateFeriasOb.fornecedorId)
                }
              : null)

          setFormData({
            compradorPrincipal: compradorPrincipal || null,
            compradorFerias: compradorFerias || null,
            fornecedor: fornecedor || null,
            de: updateFeriasOb.dataInicio?.split('T')[0] || '',
            ate: updateFeriasOb.dataFim?.split('T')[0] || ''
          })
        }
      } catch {
        setFornecedoresOptions([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchFornecedores()
    // Carrega ao abrir; data.users/updateFeriasOb são lidos no momento da abertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <>
      <Button
        fontSize={14}
        size="sm"
        marginLeft={2}
        leftIcon={<IoIosAddCircle />}
        colorScheme="blue"
        variant="outline"
        onClick={onOpen}
      >
        {updateFeriasOb ? 'Editar ' : 'Adicionar'}
      </Button>

      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          onClose()
          clearForm()
        }}
        scrollBehavior="inside"
        motionPreset="slideInBottom"
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            p={4}
            textColor={'gray.200'}
            background={'#1d5840'}
            borderTopLeftRadius={5}
            borderTopRightRadius={5}
            borderBottom="3px solid #ECC94B"
          >
            <Flex align="center">
              <Box p={2}>
                <GiIsland size="1.5em" />
              </Box>
              <Box fontWeight="bold">
                {updateFeriasOb
                  ? 'Editar Compradores Férias'
                  : 'Atribuir Compradores Férias'}
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody bg="white" pt={4} py={4}>
            {isLoading ? (
              <Flex justify="center" align="center" py={10}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <>
                <FormControl isRequired mb={4}>
                  <Filter
                    label="Comprador Backup"
                    options={data.users}
                    isMulti={false}
                    value={
                      formData.compradorPrincipal
                        ? [formData.compradorPrincipal]
                        : []
                    }
                    onChange={value =>
                      onUpdate('compradorPrincipal', value as IValue)
                    }
                    isDisabled={!!updateFeriasOb}
                  />
                </FormControl>

                <FormControl isRequired mb={4}>
                  <Filter
                    label="Comprador Férias"
                    options={data.users}
                    isMulti={false}
                    value={
                      formData.compradorFerias ? [formData.compradorFerias] : []
                    }
                    onChange={value =>
                      onUpdate('compradorFerias', value as IValue)
                    }
                  />
                </FormControl>

                <FormControl isRequired mb={4}>
                  <Filter
                    label="Fornecedor"
                    options={fornecedoresOptions}
                    isMulti={false}
                    value={formData.fornecedor ? [formData.fornecedor] : []}
                    onChange={value => onUpdate('fornecedor', value as IValue)}
                  />
                </FormControl>

                <FormControl isRequired mb={4}>
                  <FormLabel>De</FormLabel>
                  <Input
                    type="date"
                    borderColor="gray.400"
                    value={formData.de}
                    onChange={e => onUpdate('de', e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired mb={4}>
                  <FormLabel>Até</FormLabel>
                  <Input
                    type="date"
                    borderColor="gray.400"
                    value={formData.ate}
                    onChange={e => onUpdate('ate', e.target.value)}
                  />
                </FormControl>

                {errors.length > 0 && (
                  <Box mt={2} p={3}>
                    {errors.map((err, idx) => (
                      <Text key={idx} color="red.700" fontSize="sm">
                        {err}
                      </Text>
                    ))}
                  </Box>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter bg="white" borderBottomRadius="md">
            <Button
              variant="outline"
              mr={3}
              onClick={() => {
                onClose()
                clearForm()
              }}
            >
              Cancelar
            </Button>
            <Button
              bg="green.600"
              color="white"
              onClick={handleSubmit}
              _hover={isLoading || !isFormValid() ? {} : { bg: 'green.500' }}
              _active={{ bg: 'green.500' }}
              isDisabled={isLoading || !isFormValid()}
              isLoading={isSaving}
              _disabled={{ bg: 'gray.300', color: 'gray.500', _hover: {} }}
            >
              {updateFeriasOb ? 'Salvar Alterações' : 'Salvar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ModalCreateFeiras
